import { useCallback, useEffect } from 'react';
import {
  SelectionAdapterHook,
  SelectionContextValue,
  EntityName,
} from 'src/components/DataTable/types/DataTableSelection';
import { useDataTableContext } from 'src/components/DataTable/components/DataTableProvider/hooks/useDataTableContext';
import { useSelectionState } from './useSelectionState';

export interface UseDataTableSelectionOptions {
  entityName?: EntityName;
  primaryActionName?: string;
  secondaryActionName?: string;
  hideSelectionMessage?: boolean;
}

export function useDataTableSelection<TData = unknown>(
  adapter: SelectionAdapterHook,
  idKey: keyof TData,
  options: UseDataTableSelectionOptions = {}
): SelectionContextValue {
  const { data } = useDataTableContext<TData>();
  const {
    entityName,
    primaryActionName,
    secondaryActionName,
    hideSelectionMessage = false,
  } = options;

  const { initialSelections, isLoading, error, fetch, save } = adapter();

  useEffect(() => {
    fetch?.();
  }, [fetch]);

  const {
    currentSelections,
    isDirty,
    isAllSelected,
    isPartiallySelected,
    isSelected,
    toggleSelection,
    toggleAll,
    resetSelections,
  } = useSelectionState<TData>(data, idKey, initialSelections);

  const saveSelections = useCallback(async (): Promise<void> => {
    await save(currentSelections);
  }, [save, currentSelections]);

  return {
    initialSelections,
    currentSelections,
    isLoading,
    error,
    isDirty,
    isAllSelected,
    isPartiallySelected,
    isSelected,
    toggleSelection,
    toggleAll,
    resetSelections,
    saveSelections,
    entityName,
    primaryActionName,
    secondaryActionName,
    hideSelectionMessage,
  };
}
